import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';

import { Icon, Grid, GridColumn, Text, View, colors } from '@fairygodboss/ui';

import { ApplyButtonGroup } from './ApplyButtonGroup';
import { logoPropType, refPropType } from './constants';
import { CompanyLogo } from '../../Elements/CompanyLogo/CompanyLogo';

export const Company = ({
    companyName,
    rating,
    locationName,
    logo,
    companyUrl,
}) => (
    <View display="flex">
        <View width="60px" height="60px">
            <CompanyLogo
                src={logo?.src}
                alt="company-logo"
                href={`/company/jobs/${companyUrl}`}
            />
        </View>

        <View display="flex" pl="14px" flexDirection="column">
            <View flexDirection="row" display="flex">
                <View
                    data-qa="Jobs-Page-company-name"
                    as="p"
                    fontSize="16px"
                    fontWeight="700"
                    lineHeight="26px"
                    margin="0"
                    pr="12px"
                    color={colors.neutral[100]}
                >
                    {companyName}
                </View>
                {rating ? (
                    <>
                        <Icon
                            variant="ratingWingFilled_20"
                            outlineColor={colors.primary[60]}
                        />
                        <View
                            as="p"
                            fontSize="16px"
                            fontWeight="700"
                            lineHeight="26px"
                            margin="0"
                            pl="8px"
                            color={colors.neutral[100]}
                        >
                            {rating}
                        </View>
                    </>
                ) : undefined}
            </View>
            <Text
                variant="body1"
                pt="8px"
                lineHeight="26px"
                data-qa="Jobs-Page-location"
            >
                {locationName}
            </Text>
        </View>
    </View>
);

export const ApplyTop = forwardRef(
    (
        {
            handleSaveClick,
            isSaved,
            handleCompanyApplyClick,
            showDirectApply,
            handleQuickApplyClick,
        },
        ref
    ) => {
        return (
            <View py="medium" ref={ref}>
                <ApplyButtonGroup
                    handleSaveClick={handleSaveClick}
                    isSaved={isSaved}
                    showDirectApply={showDirectApply}
                    handleQuickApplyClick={handleQuickApplyClick}
                    handleCompanyApplyClick={handleCompanyApplyClick}
                    position="top"
                />
            </View>
        );
    }
);

ApplyTop.displayName = 'ApplyTop';

export const Header = ({
    title,
    companyName,
    companyUrl,
    rating,
    locationName,
    logo,
    handleSaveClick,
    isSaved,
    handleCompanyApplyClick,
    showDirectApply,
    handleQuickApplyClick,
    topApplyButtonGroupRef,
    jobSearchVariant,
}) => {
    return (
        <View
            pt={jobSearchVariant ? '0px' : '34px'}
            pb="8px"
            borderBottomStyle="solid"
            borderWidth="1px"
            borderColor={colors.secondary[20]}
        >
            <Grid>
                <GridColumn size={{ md: 10.5 }}>
                    <View
                        data-qa="Jobs-Page-title"
                        as="h1"
                        fontFamily="Open Sans, sans-serif"
                        fontSize="26px"
                        fontWeight="700"
                        lineHeight="38px"
                        margin="0"
                        pb="16px"
                        color={colors.neutral[100]}
                    >
                        {title}
                    </View>
                </GridColumn>
            </Grid>
            <Grid>
                <GridColumn size={{ md: 10.5 }}>
                    <Company
                        companyName={companyName}
                        rating={rating}
                        locationName={locationName}
                        logo={logo}
                        companyUrl={companyUrl}
                    />
                </GridColumn>
            </Grid>
            <ApplyTop
                isSaved={isSaved}
                handleSaveClick={handleSaveClick}
                showDirectApply={showDirectApply}
                handleQuickApplyClick={handleQuickApplyClick}
                handleCompanyApplyClick={handleCompanyApplyClick}
                ref={topApplyButtonGroupRef}
            />
        </View>
    );
};

Header.propTypes = {
    title: PropTypes.string.isRequired,
    companyName: PropTypes.string.isRequired,
    rating: PropTypes.number,
    locationName: PropTypes.string,
    companyUrl: PropTypes.string,
    logo: logoPropType,
    handleSaveClick: PropTypes.func,
    isSaved: PropTypes.bool,
    handleCompanyApplyClick: PropTypes.func,
    topApplyButtonGroupRef: refPropType,
    jobSearchVariant: PropTypes.bool,
    showDirectApply: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    handleQuickApplyClick: PropTypes.func,
};

Header.defaultProps = {
    rating: null,
    locationName: null,
    companyUrl: null,
    logo: {},
    handleSaveClick: () => {},
    showDirectApply: false,
    handleQuickApplyClick: () => {},
    isSaved: false,
    handleCompanyApplyClick: () => {},
    topApplyButtonGroupRef: { current: null },
    jobSearchVariant: false,
};
